// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PieChartActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import { toJS } from 'mobx';
import Highcharts from 'highcharts';
import {
  size,
  isArray,
  uniqueId,
  get,
  isNil,
  concat,
  first,
  nth,
  isNull,
  forIn,
  map,
  find,
  assign,
  getOr,
  forEach,
  clone,
  toNumber,
  flow,
  keys,
  sumBy,
  orderBy,
  includes,
  isNaN,
  last,
  toLower,
  head,
  isEmpty
} from 'lodash/fp';

import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
import reportPartElementUtils from 'share/utils/Izenda.ReportPartElementUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
const X_AXIS_CONTAINER_NAME = 'labels';
const Y_AXIS_CONTAINER_NAME = 'values';
const SEPARATOR_CONTAINER_NAME = 'separators';
const SORT_TYPES = constants.SORT_TYPES;
const GROUP_SEPARATOR = ';#';

export default function(props: any) {
  return {
    getChartSeries: function() {
      return getChartSeries(props);
    }
  };
}

/**
 * create data for gauge
 * @param  {any} props
 * @return {any}
 */
function getChartSeries(props: any) {
  const dataParser = props.chartData;
  const dataStructure = dataParser.dataStructure;

  const serieInfo = {
    groups: {},
    charts: []
  };

  const groupCharts = {}; // { groupName: [{ chartId: id}]}
  groupDataBySeparator();

  return {
    charts: serieInfo.charts,
    numberMetrics: size(dataStructure[Y_AXIS_CONTAINER_NAME]),
    groupCharts: groupCharts
  };

  /**
   * Add chart
   * @param {any} charts
   * @param {any} groupInfo
   */
  function addChart(charts: any, groupInfo: any) {
    const chartInfos = [];
    charts = isArray(charts) ? charts : [charts];

    charts.forEach(chart => {
      chart.id = uniqueId();
      chart.linkTo = groupInfo;
      serieInfo.charts.push(chart);
      chartInfos.push({
        id: chart.id
      });
    });
    const groupName = get('groupName', groupInfo);
    if (groupName !== undefined) {
      isNil(groupCharts[groupName]) && (groupCharts[groupName] = []);
      groupCharts[groupName] = concat(chartInfos, groupCharts[groupName]);
    }
  }

  /**
   * Group data by separator field
   */
  function groupDataBySeparator() {
    const groupingField = first(dataStructure[SEPARATOR_CONTAINER_NAME]);
    const labelField = nth(
      dataParser.expandedLevel,
      dataStructure[X_AXIS_CONTAINER_NAME]
    );
    const izMultiLevel = getOr(false, 'chartOptions.optionByType.izMultiLevel', props);
    let groupNames = [];
    let groupedData = dataParser.groupData(groupingField, groupNames);
    let groupInfo, groupRecords, groupStyle;

    if (!groupingField) {
      groupNames = ['ALL'];
      groupedData = {
        ALL: groupedData
      };
    }

    // Get data
    groupNames.forEach((groupName, groupIndex) => {
      groupRecords = groupedData[groupName];
      groupInfo = groupingField && {
        groupField: groupingField,
        groupName: groupName,
        groupIndex: groupIndex,
        isShowPanel: true,
        records: groupRecords
      };

      (props.chartType === CHART_TYPE.Donut
        || (constants.isChartType(props.chartType, CHART_TYPE.TreeMap) && izMultiLevel)
        ? createAllDrildownData
        : createChart)(groupRecords, labelField, groupInfo);
    });
  }

  /**
   * Create charts
   * @param  {any}  record
   * @param  {any} xAxisField
   * @param  {any}  groupInfo
   */
  function createChart(records: any, xAxisField: any, groupInfo: any) {
    const series = {};
    let label, originalLabelValue, value, chartName;

    records.forEach(record => {
      label = record[xAxisField.columnName];
      originalLabelValue = ReportPartUtils.getOriginalValue(xAxisField, record);

      dataStructure[Y_AXIS_CONTAINER_NAME].forEach(valueMappingField => {
        chartName = valueMappingField.fieldNameAlias;
        const isUsingCustomFormat = reportPartElementUtils.checkUsingCustomFormat(
          valueMappingField
        );
        value = isUsingCustomFormat
          ? record[
              constants.CUSTOM_FORMAT_PREFIX + valueMappingField.columnName
            ]
          : record[valueMappingField.columnName];

        if (!series[chartName]) {
          series[chartName] = {
            name: chartName,
            labelName: xAxisField.fieldNameAlias,
            nameField: xAxisField,
            yAxisField: valueMappingField,
            data: [],
            rawData: records,
            separatorField: get('groupField', groupInfo),
            separatorValue: get('groupName', groupInfo)
          };
        }

        (label || value) &&
          series[chartName].data.push({
            name: isNull(label) ? 'null' : label,
            originalNameValue: isNull(originalLabelValue) ? 'null' : originalLabelValue,
            y: value,
            record: record
          });
      });
    });

    forIn(serie => {
      addChart(
        [
          {
            commonOptions: {
              series: [serie]
            }
          }
        ],
        groupInfo
      );
    }, series);
  }

  /**
   * Get field name
   * @param {any} field
   */
  function getFieldName(field: any) {
    return get('fieldNameAlias', field);
  }

  function addDonutData(source, name, originalNameValue, val, level, groupId, valueFieldName, labelFieldName, record) {
    const {
      colors,
      optionByType: { izMultiColor },
      commonOptions: { izDisableMultiColor }
    } = props.chartOptions;
    const serieColors = colors || Highcharts.getOptions().colors;
    const index = izMultiColor && !izDisableMultiColor ? get('id', groupId) % serieColors.length : 0;

    const point = {
      name,
      originalNameValue,
      y: val,
      groupId: groupId,
      record: record,
      color: serieColors[index]
    };

    // add to source
    source.push(point);
  }

  /**
   * Create all drilldown data
   * @param {any} records
   * @param {any} labelField
   * @param {any} groupInfo
   */
  function createAllDrildownData(
    records: any,
    labelField: any,
    groupInfo: any
  ) {
    const xDatafields = dataStructure[X_AXIS_CONTAINER_NAME];
    let xAxisFields = toJS(xDatafields);

    xAxisFields = map(value => {
      const originalField = find(
        { fieldNameAlias: value.fieldNameAlias },
        xDatafields
      );
      value.reportPartElm =
        !isNil(originalField) && !isNil(value.reportPartElm)
          ? assign(value.reportPartElm, {
              isDateTimeType: get(
                'reportPartElm.isDateTimeType',
                originalField
              ),
              fieldFormatData: get(
                'reportPartElm.fieldFormatData',
                originalField
              )
            })
          : value.reportPartElm;
      return value;
    }, xAxisFields);

    const series = {};
    let serie;

    dataStructure[Y_AXIS_CONTAINER_NAME].forEach(valField => {
      series[getFieldName(valField)] = intData(valField);
    });

    try {
      parseData(xAxisFields, records);

      Object.keys(series).map(serieName => {
        serie = series[serieName];

        addChart(
          {
            commonOptions: {
              series: {
                data: getSerieByDrilldownLevel(serie.drilldownData),
                yAxisField: serie.yAxisField,
                serieName: serieName,
                separatorField: get('groupField', groupInfo),
                separatorValue: get('groupName', groupInfo)
              }
            }
          },
          groupInfo
        );
      });
    } catch (e) {
      return null;
    }

    function getSerieByDrilldownLevel(drilldownData) {
      const levels = [];
      let levelData;

      Object.keys(drilldownData).forEach(levelName => {
        levelData = drilldownData[levelName];
        levels[get('level', levelData)] = levelData;
      });

      return levels;
    }

    function intData(valField) {
      const re = {
        yAxisField: valField,
        drilldownData: {}
      };

      const drilldownData = re.drilldownData;

      xAxisFields.forEach((xField, index) => {
        drilldownData[getFieldName(xField)] = {
          data: [],
          nameField: xField,
          level: index
        };
      });

      return re;
    }

    function updateSerieData(xAxisField, data) {
      const xFieldName = getFieldName(xAxisField);
      let yFieldName, drilldownData;

      dataStructure[Y_AXIS_CONTAINER_NAME].forEach(yField => {
        yFieldName = getFieldName(yField);
        drilldownData = series[yFieldName].drilldownData[xFieldName];
        drilldownData.data = drilldownData.data.concat(
          getOr([], 'data', data[yFieldName])
        );
      });
    }

    function parseData(xAxisFields, records, parentId) {
      const groupingField = xAxisFields.shift();
      let metricsData;

      if (!groupingField || !xAxisFields.length) {
        metricsData = getMetricData(
          groupingField,
          records,
          xAxisFields.length,
          parentId
        );
        updateSerieData(groupingField, metricsData);
        return metricsData;
      }

      let groupNames = [];
      const groupedData = dataParser.groupData(
        groupingField,
        groupNames,
        records
      );

      const sortValueXAxis = get(
        'reportPartElm.properties.dataFormattings.sort',
        groupingField
      );

      if (includes(sortValueXAxis, [SORT_TYPES.ASC, SORT_TYPES.DESC])) {
        groupNames = flow(
          keys,
          orderBy(
            [item => (isNaN(toNumber(item)) ? item : toNumber(item))],
            [
              sortValueXAxis === SORT_TYPES.DESC
                ? toLower(SORT_TYPES.DESC)
                : toLower(SORT_TYPES.ASC)
            ]
          )
        )(groupedData);
      } else {
        const fieldYAxis = last(dataStructure[Y_AXIS_CONTAINER_NAME]);
        const name = fieldYAxis.columnName;
        const sortValueYAxis = get(
          'reportPartElm.properties.dataFormattings.sort',
          fieldYAxis
        );
        groupNames = flow(
          keys,
          map(key => ({
            key,
            value: sumBy(item => item[name], groupedData[key])
          })),
          orderBy(
            ['value'],
            [
              sortValueYAxis === SORT_TYPES.DESC
                ? toLower(SORT_TYPES.DESC)
                : toLower(SORT_TYPES.ASC)
            ]
          ),
          map('key')
        )(groupedData);
      }

      let index = 0;
      forEach(groupedName => {
        let groupId = {};
        const basePath = `${index}${groupedName}${GROUP_SEPARATOR}`;
        groupId.id = isNil(parentId) || isEmpty(parentId) ? index : parentId.id;
        groupId.path = isNil(parentId) || isEmpty(parentId) ? basePath : `${basePath}${parentId.path}`;
        groupedData[groupedName] = {
          groupId: groupId,
          data: parseData(
            clone(xAxisFields),
            groupedData[groupedName],
            groupId
          ),
          records: groupedData[groupedName]
        };
        index++;
      }, groupNames);

      metricsData = getMetricDataByGroup(
        groupingField,
        groupNames,
        groupedData,
        xAxisFields.length,
        parentId
      );
      updateSerieData(groupingField, metricsData);

      return metricsData;
    }

    function getMetricDataByGroup(
      groupingField,
      groupNames,
      groupData,
      level,
      parentId
    ) {
      let itemVal;
      const metricsData = {};
      let metric;
      let group;

      dataStructure[Y_AXIS_CONTAINER_NAME].forEach(yField => {
        metricsData[getFieldName(yField)] = {
          data: [],
          sum: 0
        };
      });

      groupNames.forEach(groupName => {
        group = get('data', groupData[groupName]);
        const record = flow(
          get('records'),
          head,
        )(groupData[groupName]);

        const groupNameOriginalValue = ReportPartUtils.getOriginalValue(groupingField, record);
        dataStructure[Y_AXIS_CONTAINER_NAME].forEach(yField => {
          const fieldName = getFieldName(yField);

          itemVal = toNumber(get('sum', group[fieldName]));
          metric = metricsData[fieldName];

          addDonutData(
            metric.data,
            groupName,
            groupNameOriginalValue,
            itemVal,
            level,
            get('groupId', groupData[groupName]),
            fieldName,
            getFieldName(groupingField),
            createFakeRecordForGroup(group[fieldName], yField, itemVal)
          );
          metric.sum += itemVal;
        });
      });
      return metricsData;

      function createFakeRecordForGroup(groupData, field, sum) {
        const record = get('data[0].record', groupData);
        const key = field.columnName;
        const keyPercent = 'percentage_' + key;
        const re = {};

        re[keyPercent] = sum * record[keyPercent] / record[key];
        re[key] = sum;

        return re;
      }
    }

    function getMetricData(xField, records, level, parentId) {
      const metricsData = {};

      dataStructure[Y_AXIS_CONTAINER_NAME].forEach(yField => {
        metricsData[getFieldName(yField)] = {
          data: [],
          sum: 0
        };
      });

      // get data for each point
      records.forEach(record => {
        const originalNameValue = ReportPartUtils.getOriginalValue(xField, record);
        const itemName = get(xField.columnName, record);
        const labelFieldName = getFieldName(xField);

        dataStructure[Y_AXIS_CONTAINER_NAME].forEach(yField => {
          const itemVal = toNumber(ReportPartUtils.getOriginalValue(yField, record));
          const valueFieldName = getFieldName(yField);
          const metric = metricsData[getFieldName(yField)];

          addDonutData(
            metric.data,
            itemName,
            originalNameValue,
            itemVal,
            level,
            parentId,
            valueFieldName,
            labelFieldName,
            record
          );
          metric.sum += itemVal;
        });
      });

      return metricsData;
    }
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.PieChartActions.js